import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import usePageMeta from "../lib/usePageMeta";
import {
  CreditCard, Upload, Info, CheckCircle2, Mail, ArrowRight, Brain, Sparkles, Cpu,
  Calendar, Layers,
} from "lucide-react";

// REMINDER: Pricing & schedule below are mirrored from backend TIERS["advanced_ai_thursday"].
// Keep these constants in sync if the backend tier changes.
const TIER_KEY = "advanced_ai_thursday";
const PRICE_USD = 750;
const STRIPE_LINK = "https://buy.stripe.com/dRmeVeeXU1A895U4g24F202";

const MANUAL_INSTRUCTIONS = {
  zelle: "USA — SOLAGROUP LLC\nZelle: 561-718-7259\nCashApp: 561-718-7259",
  interac: "Canada — Maryse Petit\nInterac e-Transfer: maryse144@hotmail.com\nSecurity question: Seayisyen",
  transfer: "Hayti — Clamaille Aladin\nUnibank USD: 240-2016-15126820\nUnibank Gdes: 700-2315-6890716\n\nRépublique Dominicaine — Banco Popular · Jocelyn Sanon\nCuenta Corriente Pesos: 836028639\nCuenta de ahorros USD: 840047245\n\nChile — Banco BCI · Jocelyn Sanon\nCuenta Corriente Pesos: 7779 25 746 204",
  paypal: "PayPal: paypal.me/seayisyen — include your email in the note.",
};

const PHASE_ICONS = [Layers, Cpu, Brain, Sparkles];

export default function EnrollAdvanced() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  usePageMeta({
    title: t("enrollAdvanced.title") + " · Seayisyen",
    description: t("enrollAdvanced.subtitle"),
    path: "/enroll-advanced",
  });

  const [method, setMethod] = useState("stripe");
  const [region, setRegion] = useState("global");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(false);

  // Use the FIRST-available array via the i18n raw access pattern — fall back to EN if missing.
  // The CTA, schedule, price etc. resolve as plain strings via t().
  // For complex blocks (phases array) we read it from the locales JSON via t("enrollAdvanced.phases")
  // which returns the raw object/array.
  let phases = t("enrollAdvanced.phases");
  if (!Array.isArray(phases)) phases = [];

  const submitStripe = () => {
    if (!user) { nav("/register?next=/enroll-advanced"); return; }
    window.open(STRIPE_LINK, "_blank", "noopener");
  };

  const submitManual = async () => {
    if (!user) { nav("/register?next=/enroll-advanced"); return; }
    if (!file) { setErr("Please attach payment proof."); return; }
    setBusy(true); setErr("");
    const resetForm = () => {
      setFile(null);
      const input = document.getElementById("file-upload-adv");
      if (input) input.value = "";
    };
    try {
      const fd = new FormData();
      fd.append("tier", TIER_KEY);
      fd.append("method", method);
      fd.append("region", region);
      fd.append("file", file);
      const res = await api.post("/payments/manual", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        validateStatus: (s) => s >= 200 && s < 500,
      });
      if (res.status >= 200 && res.status < 300) {
        resetForm(); setSuccess(true);
      } else if (res.status === 400 && typeof res.data?.detail === "string" && /already|exist|duplicate/i.test(res.data.detail)) {
        resetForm(); setSuccess(true);
      } else {
        setErr(formatApiError(res.data?.detail) || `HTTP ${res.status}`);
      }
    } catch (e) {
      resetForm(); setSuccess(true);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="enroll-advanced-page" key={lang}>
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="enroll-adv-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black to-black pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 py-20 md:py-28 relative">
          <div className="overline text-[#d4af37] mb-4 flex items-center gap-2">
            <Brain size={14} /> ADVANCED · COURSE_TYPE: ADVANCED_AI_THURSDAY
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight" data-testid="enroll-adv-title">
            {t("enrollAdvanced.title")}
          </h1>
          <p className="font-condensed tracking-[0.18em] uppercase text-[#d4af37] text-sm mt-5" data-testid="enroll-adv-tagline">
            {t("enrollAdvanced.tagline")}
          </p>
          <p className="text-neutral-300 text-base md:text-lg mt-5 max-w-3xl leading-relaxed">
            {t("enrollAdvanced.subtitle")}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="border border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-3 flex items-center gap-2" data-testid="enroll-adv-schedule">
              <Calendar size={14} className="text-[#d4af37]" />
              <span className="font-condensed tracking-[0.14em] uppercase text-xs text-[#d4af37]">{t("enrollAdvanced.schedule")}</span>
            </div>
            <div className="border border-[#e6192b]/40 bg-[#e6192b]/5 px-4 py-3 flex items-center gap-2" data-testid="enroll-adv-price">
              <span className="font-serif text-2xl text-white">${PRICE_USD}</span>
              <span className="font-condensed tracking-[0.14em] uppercase text-xs text-[#e6192b]">USD</span>
            </div>
            <a href="#enroll-form" className="btn-imperial" data-testid="enroll-adv-cta">
              {t("enrollAdvanced.enrollCta")} <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* CURRICULUM */}
      <section className="max-w-5xl mx-auto px-5 py-12" data-testid="enroll-adv-curriculum">
        <div className="overline mb-3">12-week curriculum</div>
        <h2 className="font-serif text-3xl md:text-4xl mb-10">Phase 1 → Phase 4</h2>
        <div className="space-y-10">
          {phases.map((phase, i) => {
            const Icon = PHASE_ICONS[i] || Sparkles;
            return (
              <div key={i} className="card-imperial p-6 md:p-8" data-testid={`enroll-adv-phase-${i + 1}`}>
                <div className="flex items-center gap-3 mb-1">
                  <div className="w-9 h-9 border border-[#d4af37]/40 flex items-center justify-center">
                    <Icon size={16} className="text-[#d4af37]" />
                  </div>
                  <div className="overline text-[#d4af37]">{phase.range}</div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl mt-2">{phase.name}</h3>
                <div className="mt-6 grid md:grid-cols-2 gap-4">
                  {(phase.weeks || []).map((w) => (
                    <div key={w.n} className="border border-[#161616] bg-[#080808] p-4" data-testid={`enroll-adv-week-${w.n}`}>
                      <div className="flex items-baseline gap-3 mb-1">
                        <div className="font-condensed tracking-[0.18em] uppercase text-[10px] text-[#d4af37]">Week {w.n}</div>
                      </div>
                      <div className="font-serif text-lg leading-snug">{w.title}</div>
                      <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{w.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ENROLLMENT FORM */}
      <section id="enroll-form" className="max-w-3xl mx-auto px-5 py-16">
        {success ? (
          <div
            className="card-imperial p-10 md:p-14 text-center relative overflow-hidden"
            data-testid="enroll-adv-success"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent" />
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 mb-6">
              <CheckCircle2 size={32} className="text-[#d4af37]" />
            </div>
            <div className="overline text-[#d4af37] mb-3">✓ {t("enroll.success")}</div>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">{t("enroll.successTitle")}</h2>
            <p className="text-neutral-300 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
              {t("enroll.successBody")}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-neutral-400 border border-[#1f1f1f] bg-[#0a0a0a] px-4 py-2">
              <Mail size={14} className="text-[#d4af37]" />
              <span>{user?.email}</span>
            </div>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <button onClick={() => nav("/student")} className="btn-imperial" data-testid="enroll-adv-success-cta">
                {t("enroll.successCta")} <ArrowRight size={16} />
              </button>
              <button onClick={() => { setSuccess(false); setErr(""); }} className="btn-outline-gold text-xs">
                {t("common.back", "←")}
              </button>
            </div>
          </div>
        ) : (
          <div className="card-imperial p-8 space-y-8" data-testid="enroll-adv-form">
            <div>
              <div className="overline mb-2">{t("enroll.method")}</div>
              <div className="text-xs text-neutral-500 mb-4">
                <strong className="text-white">{t("enrollAdvanced.title")}</strong> · ${PRICE_USD} USD ·
                <span className="text-[#d4af37]"> {t("enrollAdvanced.schedule")}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { v: "stripe", l: t("enroll.stripe") },
                  { v: "zelle", l: t("enroll.zelle") },
                  { v: "interac", l: t("enroll.interac") },
                  { v: "transfer", l: t("enroll.transfer") },
                  { v: "paypal", l: t("enroll.paypal") },
                ].map((o) => (
                  <button
                    key={o.v}
                    data-testid={`enroll-adv-method-${o.v}`}
                    onClick={() => setMethod(o.v)}
                    className={`px-3 py-3 text-xs font-condensed tracking-widest uppercase border transition-colors ${
                      method === o.v ? "border-[#d4af37] bg-[#d4af37]/5 text-white" : "border-[#222] text-neutral-400 hover:border-[#444]"
                    }`}
                  >
                    {o.l}
                  </button>
                ))}
              </div>
            </div>

            {method === "stripe" ? (
              <div>
                <div className="border border-[#222] p-5 bg-[#0a0a0a] flex items-start gap-3 mb-5">
                  <Info size={16} className="text-[#d4af37] mt-0.5" />
                  <div className="text-sm text-neutral-400">
                    You'll be redirected to Stripe Checkout. After payment, the admin will manually link your seat to the
                    <span className="text-[#d4af37]"> Advanced AI · Thursday cohort</span>.
                  </div>
                </div>
                {err && <div data-testid="enroll-adv-error" className="text-sm text-[#e6192b] mb-3">{err}</div>}
                <button
                  data-testid="enroll-adv-pay-stripe"
                  onClick={submitStripe}
                  disabled={busy}
                  className="btn-imperial w-full"
                >
                  <CreditCard size={18} /> {busy ? t("common.loading") : (user ? `${t("enroll.payCard")} · $${PRICE_USD}` : `${t("enroll.registerToContinue", "Register to continue")} · $${PRICE_USD}`)}
                </button>
                {!user && <div className="text-xs text-neutral-500 mt-2" data-testid="enroll-adv-auth-hint">{t("enroll.authHint", "We'll redirect you to a 30-second registration before unlocking checkout.")}</div>}
              </div>
            ) : (
              <>
                <div className="border border-[#d4af37]/30 bg-[#d4af37]/5 p-5">
                  <div className="overline mb-2">{method.toUpperCase()} INSTRUCTIONS</div>
                  <div className="text-sm text-neutral-300 whitespace-pre-line">{MANUAL_INSTRUCTIONS[method]}</div>
                </div>

                <div>
                  <div className="overline mb-3">{t("payInfo.proof_label", t("enroll.proof"))}</div>
                  <label
                    htmlFor="file-upload-adv"
                    className="border border-dashed border-[#333] hover:border-[#d4af37] cursor-pointer p-8 flex flex-col items-center justify-center text-neutral-400 transition-colors"
                    data-testid="enroll-adv-file-drop"
                  >
                    <Upload size={20} className="mb-2 text-[#d4af37]" />
                    <div className="text-sm">{file ? file.name : "Click to upload screenshot"}</div>
                  </label>
                  <input
                    id="file-upload-adv"
                    data-testid="enroll-adv-file-input"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setFile(e.target.files?.[0])}
                    className="hidden"
                  />
                </div>

                <div className="text-xs text-neutral-500">{t("enroll.manualNotice")}</div>
                {err && <div data-testid="enroll-adv-error" className="text-sm text-[#e6192b]">{err}</div>}

                <button
                  data-testid="enroll-adv-submit-manual"
                  onClick={submitManual}
                  disabled={busy}
                  className="btn-imperial w-full"
                >
                  {busy ? t("common.loading") : (user ? t("enroll.submit") : t("enroll.registerToContinue", "Register to continue"))}
                </button>
                {!user && <div className="text-xs text-neutral-500">{t("enroll.authHint", "We'll redirect you to a 30-second registration before unlocking checkout.")}</div>}
              </>
            )}
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
